import { OptionLabel } from '../caption'
import { FieldShell } from '../FieldShell'
import { labelToText } from '../richText'
import type { FieldComponentProps } from '../rendererContext'
import { OptionsStatus } from './OptionsStatus'
import { optionKey, valuesEqual, visibleOptions } from './optionUtils'
import { useControlMeta } from './useControlMeta'
import { useResolvedOptions } from './useResolvedOptions'

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : []
}

export function MultiCheckboxField({ field, path, value, errors }: FieldComponentProps) {
  const meta = useControlMeta(field, path, errors)
  const { options, loading, error } = useResolvedOptions(field)
  const selected = asArray(value)
  const isSelected = (v: unknown) => selected.some((s) => valuesEqual(s, v))
  const shown = visibleOptions(options, isSelected)

  if (field.display_only) {
    const labels = shown.filter((opt) => isSelected(opt.value)).map((opt) => labelToText(opt.label))
    return (
      <FieldShell field={field} path={path} errors={errors}>
        <div id={meta.id} className="form-control-plaintext">
          {labels.join(', ')}
        </div>
      </FieldShell>
    )
  }

  const toggle = (optValue: unknown, checked: boolean) => {
    const next = checked
      ? [...selected, optValue]
      : selected.filter((s) => !valuesEqual(s, optValue))
    meta.setFieldValue(path, next)
    meta.notifyBlur(path)
  }

  return (
    <FieldShell field={field} path={path} errors={errors} groupLabel>
      <div role="group" id={meta.id} aria-labelledby={meta.labelId} aria-describedby={meta.describedBy}>
        {shown.map((opt, i) => {
          const optId = `${meta.id}-${i}`
          // Input nested inside its <label> so the RX forms-universal checkbox
          // styling applies (see BooleanField).
          const labelClass = `${meta.classNames.checkLabel ?? 'label--flex'}${meta.disabled ? ' label--disabled' : ''}`
          return (
            <div className={meta.classNames.checkContainer ?? 'checkbox'} key={optionKey(opt.value)}>
              <label className={labelClass}>
                <input
                  id={optId}
                  type="checkbox"
                  className={meta.hasErrors ? 'is-invalid' : undefined}
                  checked={isSelected(opt.value)}
                  disabled={meta.disabled}
                  onChange={(event) => toggle(opt.value, event.target.checked)}
                />
                <OptionLabel label={opt.label} />
              </label>
            </div>
          )
        })}
      </div>
      <OptionsStatus loading={loading} error={error} />
    </FieldShell>
  )
}
